import type { DiscoveryApi, FetchApi } from "@backstage/core-plugin-api";

import type {
  DeploymentRequest,
  DeploymentResponse,
  Service,
  DeploymentsListResponse,
  DeploymentStatusResponse,
  RemoveDeployedInstanceResponse,
} from "./types";
import type { OrchestratorApi } from "./orchestratorApiRef";

export type OrchestratorApiErrorType = "timeout" | "network" | "response";

export class OrchestratorApiError extends Error {
  constructor(
    public readonly type: OrchestratorApiErrorType,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
  }
}

export interface OrchestratorApiClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  timeoutMs?: number;
  /**
   * Overrides the discovered backend base URL. Should not include a trailing slash.
   */
  baseUrlOverride?: string;
}

const DEFAULT_TIMEOUT_MS = 15_000;

export class OrchestratorApiClient implements OrchestratorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly timeoutMs: number;
  private readonly baseUrlOverride?: string;
  private baseUrlPromise: Promise<string> | undefined;

  constructor(options: OrchestratorApiClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.baseUrlOverride = options.baseUrlOverride?.replace(/\/$/, "");
  }

  async deploy(request: DeploymentRequest): Promise<DeploymentResponse> {
    return this.request<DeploymentResponse>("/deploy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
  }

  async getAvailableServices(): Promise<Service[]> {
    const response = await this.request<{ services: Service[] }>("/services");
    return response.services;
  }

  async getRepoServiceSuggestions(options?: { query?: string; limit?: number }): Promise<string[]> {
    const params = new URLSearchParams();
    if (options?.query) params.append("query", options.query);
    if (options?.limit) params.append("limit", options.limit.toString());

    const query = params.toString();
    const response = await this.request<{ services: string[] }>(
      `/services/repo${query ? `?${query}` : ""}`,
    );
    return response.services;
  }

  async getAvailableVersions(serviceName: string): Promise<string[]> {
    const payload = await this.request<{ versions: string[] }>(
      `/services/${encodeURIComponent(serviceName)}/versions`,
    );

    return payload.versions;
  }

  async getSuggestedVersions(
    serviceName: string,
    options?: { query?: string; limit?: number },
  ): Promise<string[]> {
    const params = new URLSearchParams();
    if (options?.query) params.append("query", options.query);
    if (options?.limit) params.append("limit", options.limit.toString());

    const query = params.toString();
    const payload = await this.request<{ versions: string[] }>(
      `/services/${encodeURIComponent(serviceName)}/tags${query ? `?${query}` : ""}`,
    );

    return payload.versions;
  }

  async getSquads(): Promise<string[]> {
    const response = await this.request<{ squads: string[] }>("/squads");
    return response.squads;
  }

  async getDeployments(filters?: {
    environment?: string | string[];
    status?: string | string[];
    lifecycleStatus?: string;
    triggered_by?: string;
    name?: string;
    squad?: string | string[];
    limit?: number;
    offset?: number;
  }): Promise<DeploymentsListResponse> {
    const params = new URLSearchParams();

    if (filters?.environment) {
      const values = Array.isArray(filters.environment)
        ? filters.environment
        : [filters.environment];
      values.forEach(v => params.append("environment", v));
    }
    if (filters?.status) {
      const values = Array.isArray(filters.status) ? filters.status : [filters.status];
      values.forEach(v => params.append("status", v));
    }
    if (filters?.lifecycleStatus) params.append("lifecycleStatus", filters.lifecycleStatus);
    if (filters?.triggered_by) params.append("triggered_by", filters.triggered_by);
    if (filters?.name) params.append("name", filters.name);
    if (filters?.squad) {
      const values = Array.isArray(filters.squad) ? filters.squad : [filters.squad];
      values.forEach(value => params.append("squad", value));
    }
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const query = params.toString();
    return this.request<DeploymentsListResponse>(`/deployments${query ? `?${query}` : ""}`);
  }

  async getDeploymentStatus(deploymentId: string): Promise<DeploymentStatusResponse> {
    return this.request<DeploymentStatusResponse>(`/deployments/${deploymentId}/status`);
  }

  async removeDeployedInstance(deploymentId: string): Promise<RemoveDeployedInstanceResponse> {
    return this.request<RemoveDeployedInstanceResponse>(
      `/deployments/${encodeURIComponent(deploymentId)}/remove`,
      {
        method: "POST",
      },
    );
  }

  private async getBaseUrl(): Promise<string> {
    if (this.baseUrlOverride) {
      return this.baseUrlOverride;
    }

    if (!this.baseUrlPromise) {
      this.baseUrlPromise = this.discoveryApi
        .getBaseUrl("orchestrator")
        .then(url => url.replace(/\/$/, ""));
    }

    return this.baseUrlPromise;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
        ...init,
        signal: controller.signal,
        credentials: "same-origin",
      });

      if (!response.ok) {
        let message = `Request failed with status ${response.status}`;

        const contentType = response.headers.get("content-type") ?? "";
        if (contentType.includes("application/json")) {
          const json = await response.json().catch(() => undefined);
          if (json && typeof (json as any).message === "string") {
            message = (json as any).message;
          } else if (json) {
            message = JSON.stringify(json);
          }
        } else {
          const body = await response.text().catch(() => "");
          if (body) {
            message = body;
          }
        }

        throw new OrchestratorApiError("response", message, response.status);
      }

      if (response.status === 204) {
        return {} as T;
      }

      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof OrchestratorApiError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw new OrchestratorApiError(
          "timeout",
          "Request timed out while contacting the orchestrator backend",
        );
      }

      const message = error instanceof Error ? error.message : "Network request failed";
      throw new OrchestratorApiError("network", message);
    } finally {
      clearTimeout(timeoutHandle);
    }
  }
}
